import React, { useState, useEffect } from "react";
import Select from "react-select";
import { useLocation, useNavigate } from "react-router-dom"; // Added useNavigate

function VideoLinks() {
    const location = useLocation();
    const navigate = useNavigate(); // For redirecting on error
    const [products, setProducts] = useState([]);
    const [productModels, setProductModels] = useState({});

    const [videoLinks, setVideoLinks] = useState([{ id: Date.now(), link: "", product: null }]);
    const [loading, setLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false); // Added for save button
    const [error, setError] = useState(null);

    const { categoryId, cutitemId } = location.state || {};
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

    // Debug info
    console.log("VideoLinks → categoryId:", categoryId);
    console.log("VideoLinks → cutitemId:", cutitemId);
    console.log("VideoLinks → apiBaseUrl:", apiBaseUrl);

    // Fetch products
    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const response = await fetch(
                    `${apiBaseUrl}/getProductByFilter/JAPAN_ACCESORIES?filter=${encodeURIComponent(cutitemId)}`
                );
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();
                setProducts(data);

                // Fetch models for each product
                data.forEach(async (product) => {
                    try {
                        const modelResponse = await fetch(
                            `${apiBaseUrl}/getProductByNewFilter/JAPAN_ACCESORIES?filter=${encodeURIComponent(
                                product.ItemName
                            )}`
                        );
                        if (!modelResponse.ok) {
                            throw new Error(`HTTP error! status: ${modelResponse.status}`);
                        }
                        const modelData = await modelResponse.json();
                        const modelOptions = modelData.map((model) => ({ value: model.model, label: model.model }));

                        setProductModels((prevModels) => ({
                            ...prevModels,
                            [product.ID]: modelOptions,
                        }));
                    } catch (modelError) {
                        console.error("Error fetching models:", modelError);
                    }
                });
            } catch (error) {
                console.error("Error fetching products or models:", error);
            }
        };

        fetchProducts();
    }, [apiBaseUrl, categoryId, cutitemId]);

    // Dropdown options
    const productOptions = products.map((product) => ({
        value: product.ID,
        label: product.ItemName,
    }));

    // Handlers
    const handleAddLink = () => {
        setVideoLinks((prev) => [...prev, { id: Date.now(), link: "", product: null }]);
    };

    const handleRemoveLink = (id) => {
        setVideoLinks((prev) => prev.filter((item) => item.id !== id));
    };

    const handleChangeLink = (id, value) => {
        setVideoLinks((prev) => prev.map((item) => (item.id === id ? { ...item, link: value } : item)));
    };

    const handleChangeProduct = (id, selectedOption) => {
        setVideoLinks((prev) => prev.map((item) => (item.id === id ? { ...item, product: selectedOption } : item)));
    };

    const handleSubmit = async () => {
        if (videoLinks.some((item) => !item.link || !item.product)) {
            alert("Please fill out all video links and select products before saving.");
            return;
        }

        setSaveLoading(true);
        const payload = videoLinks.map((item) => ({
            productId: item.product.value,
            productName: item.product.label,
            videoLink: item.link,
        }));

        console.log("📤 Saving video links:", payload);

        try {
            const response = await fetch(`${apiBaseUrl}/saveVideoLinks`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            alert("✅ Video links saved successfully!");
            setVideoLinks([{ id: Date.now(), link: "", product: null }]); // Reset
        } catch (err) {
            console.error("❌ Error saving video links:", err);
            alert("Failed to save video links.");
        } finally {
            setSaveLoading(false);
        }
    };

    // Navigate back to dashboard on critical error
    const handleBackToDashboard = () => {
        navigate("/admin-dashboard"); // Adjust path to your dashboard route
    };

    return (
        <div className="max-w-xl mx-auto mt-10 p-6 bg-white shadow-lg rounded-2xl">
            <h1 className="text-2xl font-bold text-center text-indigo-600 mb-6">Add Video Links</h1>

            {loading && <p className="text-center text-gray-500">Loading products...</p>}
            {error && (
                <div className="text-center text-red-500 mb-3">
                    <p>{error}</p>
                    <button
                        onClick={handleBackToDashboard}
                        className="mt-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition"
                    >
                        Back to Dashboard
                    </button>
                </div>
            )}

            {!loading &&
                products.length > 0 &&
                videoLinks.map((item, index) => (
                    <div key={item.id} className="flex items-center gap-3 mb-3">
                        <div className="flex-1">
                            <Select
                                options={productOptions}
                                value={item.product}
                                onChange={(option) => handleChangeProduct(item.id, option)}
                                placeholder={`Select product for link ${index + 1}`}
                                isClearable
                                isSearchable
                                className="mb-2"
                            />
                            <input
                                type="text"
                                placeholder={`Video link ${index + 1}`}
                                value={item.link}
                                onChange={(e) => handleChangeLink(item.id, e.target.value)}
                                className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            />
                        </div>
                        {videoLinks.length > 1 && (
                            <button onClick={() => handleRemoveLink(item.id)} className="text-red-500 font-bold text-xl">
                                ✕
                            </button>
                        )}
                    </div>
                ))}

            {!loading && products.length > 0 && (
                <div className="flex justify-between mt-5">
                    <button
                        onClick={handleAddLink}
                        className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition"
                        disabled={saveLoading}
                    >
                        + Add
                    </button>
                    <button
                        onClick={handleSubmit}
                        className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition"
                        disabled={saveLoading}
                    >
                        {saveLoading ? "Saving..." : "Save"}
                    </button>
                </div>
            )}
        </div>
    );
}

export default VideoLinks;
